import React from "react";
import {
  Home,
  Shield,
  Trees,
  Building2,
  TrendingUp,
  MapPin,
  CheckCircle,
  Phone,
} from "lucide-react";
import { Helmet } from "react-helmet";

const amenities = [
  {
    icon: <Home size={32} />,
    title: "Modern Villas",
    desc: "Elegant architecture with spacious layouts and premium finishes.",
  },
  {
    icon: <Shield size={32} />,
    title: "Secure Community",
    desc: "Gated environment with enhanced safety for your family.",
  },
  {
    icon: <Trees size={32} />,
    title: "Peaceful Living",
    desc: "Beautiful surroundings away from city congestion.",
  },
  {
    icon: <Building2 size={32} />,
    title: "Premium Construction",
    desc: "Built with quality materials and modern engineering standards.",
  },
  {
    icon: <TrendingUp size={32} />,
    title: "Investment Potential",
    desc: "Excellent opportunity for long-term capital appreciation.",
  },
  {
    icon: <MapPin size={32} />,
    title: "Prime Location",
    desc: "Strategically located within Faisal Hills near Islamabad.",
  },
];

export default function DeenVillasLanding() {
  return (
    <div className="bg-white overflow-hidden">
          <Helmet>
        <title>Deen Villas | EstateGhar</title>
        <meta
          name="description"
          content="Deen Villas - Luxury Villas in Islamabad"
        />
        <meta
          name="keywords"
          content="deen villas, luxury residential, projects in islamabad, real estate, property requests, buy property, rent property, real estate agents, property listings, EstateGhar"
        />
      </Helmet>
      {/* HERO SECTION */}
      <section className="relative min-h-screen">
        <img
          src="https://images.unsplash.com/photo-1600585154526-990dced4db0d"
          alt="Luxury Villa"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/50" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen flex items-center">
          <div className="max-w-4xl">
            <span className="bg-[#7A0045] text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wider uppercase">
              Faisal Hills Luxury Villas
            </span>

            <h1
              className="mt-8 text-5xl md:text-7xl font-black text-white leading-tight"
              style={{ fontFamily: "Montserrat" }}
            >
              Experience Luxury Living
              <span className="block text-[#D4AF37]">
                At Deen Villas
              </span>
            </h1>

            <p
              className="mt-8 text-xl md:text-2xl text-gray-200 leading-relaxed"
              style={{ fontFamily: "Poppins" }}
            >
              A premium villa community designed for families seeking
              elegance, comfort, security, and long-term value in one of
              Islamabad's fastest-growing residential destinations.
            </p>

            <div className="flex flex-wrap gap-4 mt-10">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-4">
                <p className="text-white text-sm">Villa Sizes</p>
                <p className="text-2xl font-bold text-white">
                  25×50 & 30×60
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-4">
                <p className="text-white text-sm">Booking</p>
                <p className="text-2xl font-bold text-white">
                  Only 30%
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-4">
                <p className="text-white text-sm">Installments</p>
                <p className="text-2xl font-bold text-white">
                  12 Months
                </p>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap gap-5">
              <a
                href="https://wa.me/923487172565"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-bold text-lg transition"
              >
                Contact on WhatsApp
              </a>

              <a
                href="#properties"
                className="border border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-black transition"
              >
                Explore Villas
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c"
              alt="Luxury Home"
              className="rounded-3xl shadow-2xl"
            />
          </div>

          <div>
            <span className="text-[#7A0045] font-bold uppercase tracking-widest">
              About Deen Villas
            </span>

            <h2 className="text-5xl font-black mt-4 text-gray-900">
              Where Modern Living Meets Timeless Comfort
            </h2>

            <p className="mt-8 text-lg text-gray-600 leading-relaxed">
              Welcome to Deen Villas, a prestigious residential project
              offering beautifully designed luxury villas in the heart of
              Faisal Hills. Every home is crafted to deliver comfort,
              sophistication, and functionality for modern families.
            </p>

            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              Whether you are searching for your dream home or a valuable
              real estate investment, Deen Villas combines premium
              construction, flexible payment options, and exceptional
              location advantages.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "Prime location within Faisal Hills",
                "Luxury villas with modern amenities",
                "Secure gated community",
                "Excellent investment opportunity",
                "Flexible installment plans",
              ].map((item) => (
                <div className="flex items-center gap-3" key={item}>
                  <CheckCircle className="text-green-500" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#7A0045] font-bold uppercase tracking-widest">
              Why Choose Deen Villas
            </span>

            <h2 className="text-5xl font-black mt-4">
              Luxury Lifestyle With Premium Amenities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {amenities.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition"
              >
                <div className="w-16 h-16 bg-[#7A0045] text-white rounded-2xl flex items-center justify-center">
                  {item.icon}
                </div>

                <h3 className="text-2xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-600 mt-4">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROPERTY OPTIONS */}
      <section id="properties" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <span className="text-[#7A0045] font-bold uppercase">
              Available Options
            </span>

            <h2 className="text-5xl font-black mt-4">
              Choose The Perfect Villa
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 mt-16">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1613977257363-707ba9348227"
                alt=""
                className="h-72 w-full object-cover"
              />

              <div className="p-8">
                <h3 className="text-3xl font-bold">
                  Executive Block Villas
                </h3>

                <p className="text-[#7A0045] text-xl font-semibold mt-2">
                  25 × 50
                </p>

                <p className="mt-4 text-gray-600">
                  Perfectly designed for modern families looking for
                  comfort, elegance, and practical living spaces.
                </p>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde"
                alt=""
                className="h-72 w-full object-cover"
              />

              <div className="p-8">
                <h3 className="text-3xl font-bold">
                  Premium C-Block Villas
                </h3>

                <p className="text-[#7A0045] text-xl font-semibold mt-2">
                  30 × 60
                </p>

                <p className="mt-4 text-gray-600">
                  Spacious premium villas offering larger living areas
                  and a luxurious lifestyle experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section className="py-24 bg-[#7A0045]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-black text-white">
            A Smart Investment For The Future
          </h2>

          <p className="text-xl text-white/90 mt-8 leading-relaxed">
            Faisal Hills continues to attract homeowners and investors
            because of its strategic location and rapid development.
            Deen Villas offers strong potential for capital growth while
            delivering a premium lifestyle for your family.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="bg-white rounded-3xl p-8">
              <h3 className="text-4xl font-black text-[#7A0045]">
                30%
              </h3>
              <p className="mt-2">Booking Amount</p>
            </div>

            <div className="bg-white rounded-3xl p-8">
              <h3 className="text-4xl font-black text-[#7A0045]">
                12 Months
              </h3>
              <p className="mt-2">Flexible Plan</p>
            </div>

            <div className="bg-white rounded-3xl p-8">
              <h3 className="text-4xl font-black text-[#7A0045]">
                Limited
              </h3>
              <p className="mt-2">Inventory Available</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-black text-white">
            Ready To Own Your Dream Villa?
          </h2>

          <p className="text-xl text-gray-300 mt-8">
            Connect with us on WhatsApp to receive complete project
            details, payment plans, villa availability, and booking
            assistance.
          </p>

          <a
            href="https://wa.me/923487172565"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-10 bg-green-500 hover:bg-green-600 px-10 py-5 rounded-full text-white text-xl font-bold transition"
          >
            <Phone size={24} />
            WhatsApp: +92 348 7172565
          </a>
        </div>
      </section>
    </div>
  );
}